import { CustomBlockMeta, CustomFieldTypes, Languages, Resource } from 'idea-toolbox';

import { User } from '../models/user.model';
import { SessionType } from './session.model';
import { FormBlockMeta, FormFieldMeta, getAnswersToChoice } from './formMeta.model';
import {
  DEFAULT_SESSION_PROPOSAL_FORM_DEF,
  DEFAULT_SESSION_PROPOSAL_FORM_DEF_PER_TYPE
} from './sessionProposalFormDefaults';

import { ServiceLanguages } from './serviceLanguages.enum';

export const LANGUAGES = new Languages({ default: ServiceLanguages.English, available: [ServiceLanguages.English] });
const DEFAULT_SESSION_REGISTRATION_BUFFER_MINUTES = 10;
const DEFAULT_MAX_SESSION_PROPOSALS_PER_USER = 3;
const DEFAULT_MAX_SPEAKERS_PER_SESSION_PROPOSAL = 3;

/**
 * The information of a proposed speaker that the admins can set as mandatory (name and email always are).
 */
export type ProposedSpeakerConfigurableFields = 'bio' | 'imageURI' | 'linkedIn';
/**
 * For each configurable information of a proposed speaker, whether it's mandatory.
 */
export type MandatorySpeakerFields = { [field in ProposedSpeakerConfigurableFields]: boolean };
const DEFAULT_MANDATORY_SPEAKER_FIELDS: MandatorySpeakerFields = { bio: true, imageURI: true, linkedIn: false };

/**
 * The personal data of the registration form that the admins can configure (name and email are always mandatory).
 */
export type RegistrationPersonalDataConfigurableFields =
  'birthDate' | 'esnCard' | 'idDocumentNumber' | 'idDocumentIssuedAt' | 'idDocumentValidUntil';
/**
 * For each configurable personal data of the registration form, whether it's displayed and whether it's mandatory.
 * Note: the ESNcard is requested only to ESNers; when filled in, it's verified against the ESNcard API.
 */
export type RegistrationPersonalDataFields = {
  [field in RegistrationPersonalDataConfigurableFields]: { show: boolean; mandatory: boolean };
};
const DEFAULT_REGISTRATION_PERSONAL_DATA_FIELDS: RegistrationPersonalDataFields = {
  birthDate: { show: true, mandatory: false },
  esnCard: { show: true, mandatory: true },
  idDocumentNumber: { show: true, mandatory: true },
  idDocumentIssuedAt: { show: true, mandatory: true },
  idDocumentValidUntil: { show: true, mandatory: true }
};

/**
 * A rule to make some questions of the registration form mandatory only based on the answer to another question.
 * The questions are identified by their path in the form: `sectionId.fieldId`.
 */
export interface RegistrationFormConditionalRule {
  /**
   * The question that controls the rule: either a checkbox (type boolean) or a choice (type enum), e.g. Yes/No.
   */
  question: string;
  /**
   * In case of a choice, the answer (one of the question's enum values) that triggers the rule.
   * Not set for checkboxes: the rule is triggered when the checkbox is ticked.
   */
  answer?: string;
  /**
   * The fields that are mandatory if (and only if) the rule is triggered.
   */
  fields: string[];
}

/**
 * The default ESN Accounts role prefix identifying the members of ESN International (every international body).
 */
export const DEFAULT_ESN_INTERNATIONAL_ROLE = 'International.';

export class Configurations extends Resource {
  static PK = 'EGM';

  /**
   * A fixed primary key.
   */
  PK: string;
  /**
   * Whether the registrations are open for ESNers.
   */
  isRegistrationOpenForESNers: boolean;
  /**
   * Whether externals and guests can register.
   */
  isRegistrationOpenForExternals: boolean;
  /**
   * Whether participants can register for sessions.
   */
  areSessionRegistrationsOpen: boolean;
  /**
   * The minimum amount of time (in minutes) a user must leave open between sessions.
   */
  sessionRegistrationBuffer: number;
  /**
   * Whether the delegation leaders can assign spots.
   */
  canCountryLeadersAssignSpots: boolean;
  /**
   * The configuration of the personal data section of the registration form (before the custom sections).
   */
  registrationPersonalDataFields: RegistrationPersonalDataFields;
  /**
   * The definition of the custom sections and questions of the registration form.
   */
  registrationFormDef: FormBlockMeta;
  /**
   * The questions of the registration form that are mandatory only based on the answer to another question.
   */
  registrationFormConditionalRules: RegistrationFormConditionalRule[];
  /**
   * The currency for the event, expressed in three letters (e.g. EUR).
   */
  currency: string;
  /**
   * The possible spot types.
   */
  spotTypes: string[];
  /**
   * The price for each spot type.
   */
  pricePerSpotTypes: Record<string, number>;
  /**
   * The stripe payment link for each spot type, if any.
   */
  stripeLinkPerSpotType: Record<string, string>;
  /**
   * The list of all the current ESN countries.
   */
  sectionCountries: string[];
  /**
   * The ESN Accounts roles identifying the members of ESN International: a user is part of ESN International if any
   * of their roles starts with one of these values (e.g. "International." matches every international body).
   */
  esnInternationalRoles: string[];
  /**
   * The minimum number of sessions that a user has to register to per day.
   */
  minSessionPerDay: number;
  /**
   * The maximum number of sessions that a user can register to.
   */
  maxNrOfSessions: number;
  /**
   * Apply the min & max limits only for Participants role.
   */
  forParticipants: boolean;
  /**
   * Apply the min & max limits only for Externals role.
   */
  forExternals: boolean;
  /**
   * Apply the min & max limits only for Speakers role.
   */
  forSpeakers: boolean;
  /**
   * Whether externals can propose sessions.
   */
  isSessionProposalOpen: boolean;
  /**
   * The maximum number of (non-rejected) session proposals a user can submit.
   */
  maxSessionProposalsPerUser: number;
  /**
   * The maximum number of speakers (moderator excluded) in a session proposal.
   */
  maxSpeakersPerSessionProposal: number;
  /**
   * Which information of the speakers of a session proposal (other than name and email) is mandatory.
   */
  sessionProposalMandatorySpeakerFields: MandatorySpeakerFields;
  /**
   * A custom block containing the definition of the common sections and fields of the session proposal form.
   */
  sessionProposalFormDef: FormBlockMeta;
  /**
   * The definition of the additional sections and fields of the session proposal form, specific to each session type.
   */
  sessionProposalFormDefPerType: { [type: string]: FormBlockMeta };

  load(x: any): void {
    super.load(x);
    this.PK = Configurations.PK;
    this.isRegistrationOpenForESNers = this.clean(x.isRegistrationOpenForESNers, Boolean);
    this.isRegistrationOpenForExternals = this.clean(x.isRegistrationOpenForExternals, Boolean);
    this.areSessionRegistrationsOpen = this.clean(x.areSessionRegistrationsOpen, Boolean);
    this.sessionRegistrationBuffer = this.clean(
      x.sessionRegistrationBuffer,
      Number,
      DEFAULT_SESSION_REGISTRATION_BUFFER_MINUTES
    );
    this.canCountryLeadersAssignSpots = this.clean(x.canCountryLeadersAssignSpots, Boolean);
    this.registrationPersonalDataFields = {} as RegistrationPersonalDataFields;
    (Object.keys(DEFAULT_REGISTRATION_PERSONAL_DATA_FIELDS) as RegistrationPersonalDataConfigurableFields[]).forEach(
      f => {
        const { show, mandatory } =
          x.registrationPersonalDataFields?.[f] ?? DEFAULT_REGISTRATION_PERSONAL_DATA_FIELDS[f];
        // a hidden field can't be mandatory
        this.registrationPersonalDataFields[f] = {
          show: Boolean(show),
          mandatory: Boolean(show) && Boolean(mandatory)
        };
      }
    );
    this.registrationFormDef = new FormBlockMeta(x.registrationFormDef, LANGUAGES);
    // the rules referring to questions or answers that don't exist (anymore) are fixed or discarded
    this.registrationFormConditionalRules = (x.registrationFormConditionalRules ?? [])
      .map((rule: any): RegistrationFormConditionalRule => {
        // backwards compatibility: the rules used to be controlled only by checkboxes (`checkbox`)
        const question = this.clean(rule?.question ?? rule?.checkbox, String);
        const isChoice = this.getRegistrationFormField(question)?.type === CustomFieldTypes.ENUM;
        return {
          question,
          ...(isChoice ? { answer: this.clean(rule?.answer, String) } : {}),
          fields: this.cleanArray(rule?.fields, String).filter(f => f !== question && this.getRegistrationFormField(f))
        };
      })
      .filter((rule: RegistrationFormConditionalRule): boolean => this.isRegistrationFormConditionalRuleValid(rule));
    this.currency = this.clean(x.currency, String);
    this.spotTypes = this.cleanArray(x.spotTypes, String);
    this.pricePerSpotTypes = {};
    this.stripeLinkPerSpotType = {};
    if (x.pricePerSpotTypes)
      this.spotTypes.forEach(st => (this.pricePerSpotTypes[st] = this.clean(x.pricePerSpotTypes[st], Number, 0)));
    if (x.stripeLinkPerSpotType)
      this.spotTypes.forEach(st => (this.stripeLinkPerSpotType[st] = this.clean(x.stripeLinkPerSpotType[st], String)));
    this.sectionCountries = this.cleanArray(x.sectionCountries, String);
    this.esnInternationalRoles = this.cleanArray(x.esnInternationalRoles, String)
      .map(r => r.trim())
      .filter(r => r);
    if (!this.esnInternationalRoles.length) this.esnInternationalRoles = [DEFAULT_ESN_INTERNATIONAL_ROLE];
    this.minSessionPerDay = this.clean(x.minSessionPerDay, Number, 2);
    this.maxNrOfSessions = this.clean(x.maxNrOfSessions, Number, 7);
    this.forParticipants = this.clean(x.forParticipants, Boolean);
    this.forExternals = this.clean(x.forExternals, Boolean);
    this.forSpeakers = this.clean(x.forSpeakers, Boolean);
    this.isSessionProposalOpen = this.clean(x.isSessionProposalOpen, Boolean);
    this.maxSessionProposalsPerUser = this.clean(
      x.maxSessionProposalsPerUser,
      Number,
      DEFAULT_MAX_SESSION_PROPOSALS_PER_USER
    );
    this.maxSpeakersPerSessionProposal = this.clean(
      x.maxSpeakersPerSessionProposal,
      Number,
      DEFAULT_MAX_SPEAKERS_PER_SESSION_PROPOSAL
    );
    this.sessionProposalMandatorySpeakerFields = { ...DEFAULT_MANDATORY_SPEAKER_FIELDS };
    (Object.keys(DEFAULT_MANDATORY_SPEAKER_FIELDS) as ProposedSpeakerConfigurableFields[]).forEach(f => {
      const isMandatory = x.sessionProposalMandatorySpeakerFields?.[f];
      if (isMandatory !== undefined) this.sessionProposalMandatorySpeakerFields[f] = Boolean(isMandatory);
    });
    // the defaults (from the EGM Session Proposal Form) apply until the admins save a definition, even an empty one
    this.sessionProposalFormDef = new FormBlockMeta(
      x.sessionProposalFormDef ?? DEFAULT_SESSION_PROPOSAL_FORM_DEF,
      LANGUAGES
    );
    this.sessionProposalFormDefPerType = {};
    Object.values(SessionType)
      .filter(t => t !== SessionType.COMMON)
      .forEach(t => {
        this.sessionProposalFormDefPerType[t] = new FormBlockMeta(
          x.sessionProposalFormDefPerType?.[t] ?? DEFAULT_SESSION_PROPOSAL_FORM_DEF_PER_TYPE[t],
          LANGUAGES
        );
      });
  }

  safeLoad(newData: any, safeData: any): void {
    super.safeLoad(newData, safeData);
    this.PK = safeData.PK;
  }

  validate(): string[] {
    const e = super.validate();
    this.registrationFormDef.validate(LANGUAGES).forEach(ea => e.push(`registrationFormDef.${ea}`));
    // note: when loaded, the invalid rules are discarded; here we catch the ones being configured, before they're lost
    this.registrationFormConditionalRules.forEach((rule, index) => {
      if (!this.isRegistrationFormConditionalRuleValid(rule)) e.push(`registrationFormConditionalRules[${index}]`);
    });
    if (this.sessionRegistrationBuffer < 0) e.push('sessionRegistrationBuffer');
    if (this.maxSessionProposalsPerUser < 1) e.push('maxSessionProposalsPerUser');
    if (this.maxSpeakersPerSessionProposal < 1) e.push('maxSpeakersPerSessionProposal');
    this.sessionProposalFormDef.validate(LANGUAGES).forEach(ea => e.push(`sessionProposalFormDef.${ea}`));
    Object.entries(this.sessionProposalFormDefPerType).forEach(([type, def]) =>
      def.validate(LANGUAGES).forEach(ea => e.push(`sessionProposalFormDefPerType.${type}.${ea}`))
    );
    return e;
  }

  /**
   * Load a registration form to use in the UI.
   */
  loadRegistrationForm(registrationDef: CustomBlockMeta, existingForm?: any): CustomBlockMeta {
    return existingForm ? registrationDef.loadSections(existingForm) : registrationDef.setSectionsDefaultValues();
  }

  /**
   * Get the definition of a question of the registration form by its path (`sectionId.fieldId`), if it exists.
   */
  getRegistrationFormField(path: string): FormFieldMeta | undefined {
    const [sectionId, fieldId] = String(path).split('.');
    return this.registrationFormDef.sections[sectionId]?.fields[fieldId];
  }
  /**
   * Whether a conditional rule is complete: it's controlled by a checkbox or by an existing answer to a choice,
   * and it makes mandatory at least one question.
   */
  isRegistrationFormConditionalRuleValid(rule: RegistrationFormConditionalRule): boolean {
    const question = this.getRegistrationFormField(rule?.question);
    const canControlRule =
      question?.type === CustomFieldTypes.BOOLEAN ||
      (question?.type === CustomFieldTypes.ENUM && question.enum?.includes(rule.answer));
    return canControlRule && !!rule.fields?.length;
  }
  /**
   * Validate the custom sections of a registration form; it returns the questions (`sectionId.fieldId`) with errors.
   * The questions subject to a conditional rule are mandatory if (and only if) the rule is triggered.
   */
  validateRegistrationForm(form: any): string[] {
    const getValue = (path: string): any => {
      const [sectionId, fieldId] = path.split('.');
      return form?.[sectionId]?.[fieldId];
    };
    const isEmpty = (value: any): boolean => (!value && value !== 0) || !String(value).trim().length;

    const errors = new Set(this.registrationFormDef.validateSections(form));
    this.registrationFormConditionalRules.forEach(rule => {
      // a checkbox triggers the rule when ticked, a choice when the answer is (or includes) the one configured
      const isTriggered = rule.answer
        ? getAnswersToChoice(getValue(rule.question)).includes(rule.answer)
        : !!getValue(rule.question);
      rule.fields.forEach(f => {
        if (!isTriggered) errors.delete(f);
        else if (isEmpty(getValue(f))) errors.add(f);
      });
    });
    return Array.from(errors);
  }

  /**
   * Whether a personal data field of the registration form is requested to the user.
   * Note: the ESNcard is requested only to ESNers.
   */
  isRegistrationPersonalDataFieldShown(field: RegistrationPersonalDataConfigurableFields, user: User): boolean {
    if (field === 'esnCard' && user.isExternal()) return false;
    return this.registrationPersonalDataFields[field].show;
  }
  /**
   * Whether a personal data field of the registration form is mandatory for the user.
   */
  isRegistrationPersonalDataFieldMandatory(field: RegistrationPersonalDataConfigurableFields, user: User): boolean {
    return (
      this.isRegistrationPersonalDataFieldShown(field, user) && this.registrationPersonalDataFields[field].mandatory
    );
  }
  /**
   * Validate the personal data section of a user's registration; it returns the fields with errors.
   * Note: the validity of the ESNcard against the ESNcard API is checked separately, by the back-end.
   */
  validateRegistrationPersonalData(user: User): string[] {
    const e: string[] = [];
    if (this.isRegistrationPersonalDataFieldMandatory('birthDate', user) && !user.birthDate) e.push('birthDate');
    if (this.isRegistrationPersonalDataFieldMandatory('idDocumentNumber', user) && !user.idDocumentNumber)
      e.push('idDocumentNumber');
    if (this.isRegistrationPersonalDataFieldMandatory('idDocumentIssuedAt', user) && !user.idDocumentIssuedAt)
      e.push('idDocumentIssuedAt');
    const isExpiryMissing =
      this.isRegistrationPersonalDataFieldMandatory('idDocumentValidUntil', user) && !user.idDocumentValidUntil;
    // a document can't expire before being issued
    const isExpiryBeforeIssue =
      user.idDocumentIssuedAt && user.idDocumentValidUntil && user.idDocumentValidUntil <= user.idDocumentIssuedAt;
    if (isExpiryMissing || isExpiryBeforeIssue) e.push('idDocumentValidUntil');
    if (this.isRegistrationPersonalDataFieldShown('esnCard', user)) {
      const isMissing = !user.esnCard && this.isRegistrationPersonalDataFieldMandatory('esnCard', user);
      if (isMissing || (user.esnCard && !User.isESNcardFormatValid(user.esnCard))) e.push('esnCard');
    }
    return e;
  }

  /**
   * Whether registrations are open based on user type
   */
  canUserRegister(user: User): boolean {
    return user.isExternal() ? this.isRegistrationOpenForExternals : this.isRegistrationOpenForESNers;
  }

  /**
   * Whether the user can propose sessions: externals only, when proposals are open and after having submitted
   * their registration to the event.
   */
  canUserProposeSession(user: User): boolean {
    return this.isSessionProposalOpen && user.isExternal() && !!user.registrationAt;
  }
  /**
   * Whether the only thing preventing the user from proposing sessions is the missing registration to the event.
   */
  isRegistrationMissingToProposeSession(user: User): boolean {
    return this.isSessionProposalOpen && user.isExternal() && !user.registrationAt;
  }

  /**
   * Returns the payment link associated with the spot type.
   */
  getSpotPaymentLink(spotType: string): string {
    if (!(this.stripeLinkPerSpotType || spotType)) return;
    return this.stripeLinkPerSpotType[spotType];
  }

  /**
   * Returns the price associated with the spot type.
   */
  /**
   * Whether a list of roles from ESN Accounts identifies a member of ESN International.
   */
  isESNInternationalByRoles(roles: string[]): boolean {
    return (roles ?? []).some(role => this.esnInternationalRoles.some(prefix => role.startsWith(prefix)));
  }
  getSpotPrice(spotType: string): number {
    if (!(this.pricePerSpotTypes || spotType)) return;
    return this.pricePerSpotTypes[spotType];
  }
}

/**
 * The types of email templates available.
 */
export enum EmailTemplates {
  SPOT_ASSIGNED = 'spot-assigned',
  REGISTRATION_CONFIRMED = 'registration-confirmed',
  SPOT_TRANSFERRED = 'spot-transferred',
  SPOT_RELEASED = 'spot-released',
  PROPOSAL_RECEIVED = 'proposal-received',
  PROPOSAL_APPROVED = 'proposal-approved',
  PROPOSAL_APPROVED_WITH_CONDITIONS = 'proposal-approved-conditions',
  PROPOSAL_REJECTED = 'proposal-rejected'
}

/**
 * The types of document templates available.
 */
export enum DocumentTemplates {
  INVOICE = 'INVOICE'
}
